import type { ConsentData } from '@/core/types';

export class ScriptInterceptor {
  private static instance: ScriptInterceptor | null = null;
  private originalCreateElement: typeof document.createElement;
  private originalAppendChild: typeof Node.prototype.appendChild;
  private originalInsertBefore: typeof Node.prototype.insertBefore;
  private isIntercepting = false;
  private consent: ConsentData | null = null;

  private constructor() {
    this.originalCreateElement = document.createElement.bind(document);
    this.originalAppendChild = Node.prototype.appendChild;
    this.originalInsertBefore = Node.prototype.insertBefore;
  }

  public static getInstance(): ScriptInterceptor {
    if (!ScriptInterceptor.instance) {
      ScriptInterceptor.instance = new ScriptInterceptor();
    }
    return ScriptInterceptor.instance;
  }

  public startIntercepting(consent?: ConsentData): void {
    if (this.isIntercepting) return;

    this.isIntercepting = true;
    this.consent = consent || null;

    this.interceptCreateElement();
    this.interceptDOMManipulation();
    this.interceptGlobalObjects();
  }

  public stopIntercepting(): void {
    if (!this.isIntercepting) return;

    this.isIntercepting = false;
    this.restoreOriginalMethods();
  }

  public updateConsent(consent: ConsentData): void {
    this.consent = consent;
  }

  private interceptCreateElement(): void {
    document.createElement = ((
      tagName: string,
      options?: ElementCreationOptions
    ) => {
      const element = this.originalCreateElement(tagName, options);

      if (tagName.toLowerCase() === 'script') {
        this.wrapScriptElement(element as HTMLScriptElement);
      }

      return element;
    }) as typeof document.createElement;
  }

  private interceptDOMManipulation(): void {
    // Store reference to original methods and interceptor instance
    const originalAppendChild = this.originalAppendChild;
    const interceptorInstance = this;

    Node.prototype.appendChild = function <T extends Node>(newChild: T): T {
      try {
        if (
          newChild.nodeType === Node.ELEMENT_NODE &&
          (newChild as unknown as Element).tagName === 'SCRIPT'
        ) {
          const script = newChild as unknown as HTMLScriptElement;

          if (interceptorInstance.shouldBlockScript(script)) {
            // Remove the src to prevent loading
            if (script.src) {
              script.removeAttribute('src');
              script.src = ''; // Ensure src is completely empty
            }
            // Still append the element but without src
            return originalAppendChild.call(this, newChild) as T;
          }
        }

        // Use call with proper this context
        return originalAppendChild.call(this, newChild) as T;
      } catch (error) {
        console.warn(
          'ConsentManager: Error in appendChild interceptor:',
          error
        );
        // Fallback to original method with proper context
        return originalAppendChild.call(this, newChild) as T;
      }
    };

    const originalInsertBefore = this.originalInsertBefore;

    Node.prototype.insertBefore = function <T extends Node>(
      newChild: T,
      refChild: Node | null
    ): T {
      try {
        if (
          newChild.nodeType === Node.ELEMENT_NODE &&
          (newChild as unknown as Element).tagName === 'SCRIPT'
        ) {
          const script = newChild as unknown as HTMLScriptElement;

          if (interceptorInstance.shouldBlockScript(script)) {
            // Remove the src to prevent loading
            if (script.src) {
              script.removeAttribute('src');
              script.src = ''; // Ensure src is completely empty
            }
            // Still insert the element but without src
            return originalInsertBefore.call(this, newChild, refChild) as T;
          }
        }

        // Use call with proper this context
        return originalInsertBefore.call(this, newChild, refChild) as T;
      } catch (error) {
        console.warn(
          'ConsentManager: Error in insertBefore interceptor:',
          error
        );
        // Fallback to original method with proper context
        return originalInsertBefore.call(this, newChild, refChild) as T;
      }
    };
  }

  private interceptGlobalObjects(): void {
    this.interceptGoogleAnalytics();
    this.interceptGoogleTagManager();
    this.interceptFacebookPixel();

    // Set up property watchers for global objects that don't exist yet
    this.setupGlobalPropertyWatchers();
  }

  private setupGlobalPropertyWatchers(): void {
    // Watch for gtag creation
    if (!(window as any).gtag) {
      Object.defineProperty(window, 'gtag', {
        set: (value) => {
          Object.defineProperty(window, 'gtag', {
            value: (...args: any[]) => {
              if (!this.hasAnalyticsConsent()) {
                console.log(
                  '🚫 ConsentManager v1.0.2: Blocked gtag call - no analytics consent'
                );
                return;
              }
              return value.apply(window, args);
            },
            writable: true,
            configurable: true,
          });
        },
        get: () => undefined,
        configurable: true,
      });
    }

    // Watch for fbq creation
    if (!(window as any).fbq) {
      Object.defineProperty(window, 'fbq', {
        set: (value) => {
          Object.defineProperty(window, 'fbq', {
            value: (...args: any[]) => {
              if (!this.hasAdvertisingConsent()) {
                console.log(
                  '🚫 ConsentManager v1.0.2: Blocked fbq call - no advertising consent'
                );
                return;
              }
              return value.apply(window, args);
            },
            writable: true,
            configurable: true,
          });
        },
        get: () => undefined,
        configurable: true,
      });
    }
  }

  private interceptGoogleAnalytics(): void {
    try {
      if ((window as any).gtag) {
        const originalGtag = (window as any).gtag;
        (window as any).gtag = (...args: any[]) => {
          if (!this.hasAnalyticsConsent()) {
            console.log(
              'ConsentManager: Blocked gtag call without analytics consent'
            );
            return;
          }
          return originalGtag.apply(window, args);
        };
      }

      if ((window as any).ga) {
        const originalGa = (window as any).ga;
        (window as any).ga = (...args: any[]) => {
          if (!this.hasAnalyticsConsent()) {
            console.log(
              'ConsentManager: Blocked ga call without analytics consent'
            );
            return;
          }
          return originalGa.apply(window, args);
        };
      }
    } catch (error) {
      console.warn(
        'ConsentManager: Error intercepting Google Analytics:',
        error
      );
    }
  }

  private interceptGoogleTagManager(): void {
    try {
      const originalDataLayer = (window as any).dataLayer || [];
      const interceptorInstance = this;

      (window as any).dataLayer = new Proxy(originalDataLayer, {
        set: (target: any[], property: string | symbol, value: any) => {
          if (property === 'push' && typeof value === 'function') {
            const originalPush = value;
            target[property] = (...args: any[]) => {
              if (interceptorInstance.shouldAllowDataLayerPush(args[0])) {
                return originalPush.apply(target, args);
              } else {
                console.log(
                  'ConsentManager: Blocked dataLayer.push without consent'
                );
              }
            };
            return true;
          }
          target[property as any] = value;
          return true;
        },
      });
    } catch (error) {
      console.warn(
        'ConsentManager: Error intercepting Google Tag Manager:',
        error
      );
    }
  }

  private interceptFacebookPixel(): void {
    try {
      if ((window as any).fbq) {
        const originalFbq = (window as any).fbq;
        (window as any).fbq = (...args: any[]) => {
          if (!this.hasAdvertisingConsent()) {
            console.log(
              'ConsentManager: Blocked fbq call without advertising consent'
            );
            return;
          }
          return originalFbq.apply(window, args);
        };
      }
    } catch (error) {
      console.warn('ConsentManager: Error intercepting Facebook Pixel:', error);
    }
  }

  private wrapScriptElement(script: HTMLScriptElement): void {
    const originalSrcSetter = Object.getOwnPropertyDescriptor(
      HTMLScriptElement.prototype,
      'src'
    )?.set;

    if (originalSrcSetter) {
      Object.defineProperty(script, 'src', {
        set: (value: string) => {
          if (this.shouldBlockScript(script, value)) {
            return;
          }
          originalSrcSetter.call(script, value);
        },
        get: () => {
          return script.getAttribute('src') || '';
        },
        configurable: true,
      });
    }
  }

  private shouldBlockScript(script: HTMLScriptElement, src?: string): boolean {
    const scriptSrc = src || script.src || '';

    // If not intercepting, allow all scripts
    if (!this.isIntercepting) {
      return false;
    }

    // If no src, allow it (inline scripts)
    if (!scriptSrc) {
      return false;
    }

    const category = this.categorizeScript(scriptSrc);

    // Essential scripts are always allowed
    if (category === 'essential') {
      return false;
    }

    // If no consent data yet, block non-essential scripts
    if (!this.consent) {
      return true;
    }

    // Block if consent for this category is not explicitly true
    return this.consent[category] !== true;
  }

  private shouldAllowDataLayerPush(data: any): boolean {
    if (!this.consent) return false;

    if (data && typeof data === 'object') {
      if (data.event === 'gtm.js' || data.event === 'gtm.load') {
        return true;
      }

      if (
        data.event?.includes('purchase') ||
        data.event?.includes('conversion')
      ) {
        return this.hasAdvertisingConsent();
      }

      if (data.event?.includes('page_view') || data.event?.includes('scroll')) {
        return this.hasAnalyticsConsent();
      }
    }

    return this.hasAnalyticsConsent();
  }

  private categorizeScript(src: string): keyof ConsentData {
    const lowerSrc = src.toLowerCase();

    if (this.isEssentialScript(lowerSrc)) return 'essential';

    // Check advertising first for specific patterns that might conflict with analytics
    if (this.isAdvertisingScript(lowerSrc)) return 'advertising';
    if (this.isAnalyticsScript(lowerSrc)) return 'analytics';
    if (this.isFunctionalScript(lowerSrc)) return 'functional';
    if (this.isPersonalizationScript(lowerSrc)) return 'personalization';

    return 'functional';
  }

  private isEssentialScript(src: string): boolean {
    const essentialPatterns = [
      'recaptcha',
      'stripe.com',
      'paypal.com',
      'checkout',
      'payment',
      'security',
      'googletagmanager.com', // GTM container should always be essential
      'gtag', // gtag.js should be essential
    ];
    return essentialPatterns.some((pattern) => src.includes(pattern));
  }

  private isAnalyticsScript(src: string): boolean {
    const analyticsPatterns = [
      'google-analytics.com',
      // Removed 'googletagmanager.com' and 'gtag' - these should be essential
      'hotjar.com',
      'mixpanel.com',
      'segment.com',
      'analytics',
    ];
    return analyticsPatterns.some((pattern) => src.includes(pattern));
  }

  private isAdvertisingScript(src: string): boolean {
    const advertisingPatterns = [
      'facebook.net',
      'twitter.com',
      'licdn.com',
      'linkedin.com',
      'bing.com',
      'pinterest.com',
      'tiktok.com',
      'ads',
      'advertising',
      'doubleclick',
    ];
    return advertisingPatterns.some((pattern) => src.includes(pattern));
  }

  private isFunctionalScript(src: string): boolean {
    const functionalPatterns = [
      'youtube.com',
      'maps.googleapis.com',
      'intercom.io',
      'zendesk.com',
      'drift.com',
      'chat',
      'support',
    ];
    return functionalPatterns.some((pattern) => src.includes(pattern));
  }

  private isPersonalizationScript(src: string): boolean {
    const personalizationPatterns = [
      'personalization',
      'recommendation',
      'optimize',
      'ab-test',
    ];
    return personalizationPatterns.some((pattern) => src.includes(pattern));
  }

  private hasAnalyticsConsent(): boolean {
    return this.consent?.analytics === true;
  }

  private hasAdvertisingConsent(): boolean {
    return this.consent?.advertising === true;
  }

  private restoreOriginalMethods(): void {
    document.createElement = this.originalCreateElement;
    Node.prototype.appendChild = this.originalAppendChild;
    Node.prototype.insertBefore = this.originalInsertBefore;
  }

  public destroy(): void {
    this.stopIntercepting();
    ScriptInterceptor.instance = null;
  }
}
